'use client';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Command as CommandPrimitive } from 'cmdk';
import { Check } from 'lucide-react';
import { cn } from 'shared/lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from 'shared/ui/command';
import type { InputProps } from 'shared/ui/input';
import { Input } from 'shared/ui/input';
import { Popover, PopoverContent } from 'shared/ui/popover';
import { useState } from 'react';
import * as React from 'react';
export interface ISuggestion {
  label: string;
  value: string;
}
interface InputWithSuggestionsProps extends InputProps {
  suggestions?: ISuggestion[];
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
}
export default function InputWithSuggestions({
  suggestions,
  value: externalValue,
  onChange: externalOnChange,
  onBlur: externalOnBlur,
  ...props
}: InputWithSuggestionsProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(externalValue || '');
  const [value, setValue] = useState('');
  const [hasSelection, setHasSelection] = useState(false);

  // Sync external value with internal search state
  React.useEffect(() => {
    if (externalValue !== undefined) {
      setSearch(externalValue);
    }
  }, [externalValue]);
  if (!suggestions?.length) {
    return <Input value={search} onChange={e => {
      setSearch(e.target.value);
      externalOnChange?.(e);
    }} onBlur={externalOnBlur} {...props} />;
  }
  return <div className="flex items-center" data-sentry-component="InputWithSuggestions" data-sentry-source-file="input-with-suggestions.tsx">
      <Popover open={open} onOpenChange={setOpen} data-sentry-element="Popover" data-sentry-source-file="input-with-suggestions.tsx">
        <Command shouldFilter={false} className="!bg-transparent" data-sentry-element="Command" data-sentry-source-file="input-with-suggestions.tsx">
          <PopoverPrimitive.Anchor asChild data-sentry-element="unknown" data-sentry-source-file="input-with-suggestions.tsx">
            <CommandPrimitive.Input asChild {...props} value={search} onValueChange={newValue => {
            setSearch(newValue);
            const event = {
              target: {
                value: newValue
              }
            } as React.ChangeEvent<HTMLInputElement>;
            externalOnChange?.(event);
            setHasSelection(false);
            // Clear value if the new input doesn't match any suggestion's label
            const matchingSuggestion = suggestions.find(suggestion => suggestion.label === newValue);
            if (!matchingSuggestion) {
              setValue('');
            }
          }} onKeyDown={e => setOpen(e.key !== 'Escape')} onMouseDown={() => setOpen(open => !!search || !open)} onFocus={() => setOpen(true)} onBlur={e => {
            if (!e.relatedTarget?.hasAttribute('cmdk-list')) {
              // Only update if there was an actual selection from the dropdown
              if (hasSelection && value) {
                const selectedSuggestion = suggestions.find(suggestion => suggestion.value === value);
                if (selectedSuggestion) {
                  setSearch(selectedSuggestion.label);
                  const event = {
                    target: {
                      value: selectedSuggestion.label
                    }
                  } as React.ChangeEvent<HTMLInputElement>;
                  externalOnChange?.(event);
                }
              }
              setOpen(false);
              externalOnBlur?.();
            }
          }} data-sentry-element="unknown" data-sentry-source-file="input-with-suggestions.tsx">
              <Input className="w-full" data-sentry-element="Input" data-sentry-source-file="input-with-suggestions.tsx" />
            </CommandPrimitive.Input>
          </PopoverPrimitive.Anchor>
          {!open && <CommandList aria-hidden="true" className="hidden" />}
          <PopoverContent asChild onOpenAutoFocus={e => e.preventDefault()} onInteractOutside={e => {
          if (e.target instanceof Element && e.target.hasAttribute('cmdk-input')) {
            e.preventDefault();
          }
        }} className="w-[--radix-popover-trigger-width] p-0" data-sentry-element="PopoverContent" data-sentry-source-file="input-with-suggestions.tsx">
            <CommandList data-sentry-element="CommandList" data-sentry-source-file="input-with-suggestions.tsx">
              <CommandEmpty className="p-0 h-2" data-sentry-element="CommandEmpty" data-sentry-source-file="input-with-suggestions.tsx" />
              <CommandGroup data-sentry-element="CommandGroup" data-sentry-source-file="input-with-suggestions.tsx">
                {suggestions.map(suggestion => <CommandItem key={suggestion.value} value={suggestion.value} onMouseDown={e => e.preventDefault()} onSelect={currentValue => {
                setValue(currentValue === value ? '' : currentValue);
                setSearch(currentValue === value ? '' : suggestions.find(suggestion => suggestion.value === currentValue)?.label ?? '');
                externalOnChange?.({
                  target: {
                    value: currentValue
                  }
                } as React.ChangeEvent<HTMLInputElement>);
                setHasSelection(true);
                externalOnBlur?.();
                setOpen(false);
              }}>
                    <Check className={cn('mr-2 size-4', value === suggestion.value ? 'opacity-100' : 'opacity-0')} />
                    {suggestion.label}
                  </CommandItem>)}
              </CommandGroup>
            </CommandList>
          </PopoverContent>
        </Command>
      </Popover>
    </div>;
}